import React, { useState, useEffect } from 'react';
import { 
  Settings, Save, Loader, CheckCircle, AlertTriangle, 
  Trash2, UserX, Download, Key, Shield, X 
} from 'lucide-react';
import { preferencesAPI } from '../services/api';
import SubscriptionSettings from './SubscriptionSettings';

// Add accountAPI to your api.js imports or define inline
const API_URL = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

const accountAPI = {
  getAccountStatus: async (token) => {
    const response = await fetch(`${API_URL}/users/account-status`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch account status');
    }
    return response.json();
  },

  deactivateAccount: async (token, password, reason = '') => {
    const response = await fetch(`${API_URL}/users/deactivate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ password, reason })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to deactivate account');
    return data;
  },

  deleteAccount: async (token, password, confirmation) => {
    const response = await fetch(`${API_URL}/users/delete`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ password, confirmation })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to delete account');
    return data;
  },

  changePassword: async (token, currentPassword, newPassword) => {
    const response = await fetch(`${API_URL}/users/change-password`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ currentPassword, newPassword })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to change password');
    return data;
  },

  exportData: async (token) => {
    const response = await fetch(`${API_URL}/users/export-data`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to export data');
    }
    return response.json();
  }
};

const Preferences = ({ user, token, onPreferencesChange, onLogout }) => {
  const [preferences, setPreferences] = useState({
    default_timeframe: 'daily',
    default_tab: 'market',
    currency: 'USD',
    email_notifications: true,
    price_alert_emails: true,
    daily_summary_emails: false,
    theme: 'dark'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  
  // Account management state
  const [accountStatus, setAccountStatus] = useState(null);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  
  // Form state for modals
  const [password, setPassword] = useState('');
  const [deactivateReason, setDeactivateReason] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  useEffect(() => {
    if (user) {
      fetchPreferences();
      fetchAccountStatus();
    }
  }, [user]);

  const fetchPreferences = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const data = await preferencesAPI.getPreferences(user.id);
      setPreferences(data);
    } catch (error) {
      console.error('Failed to fetch preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccountStatus = async () => {
    if (!token) return;
    
    try {
      const status = await accountAPI.getAccountStatus(token);
      setAccountStatus(status);
    } catch (error) {
      console.error('Failed to fetch account status:', error);
    }
  };

  const handleSave = async () => {
    if (!user) return;

    setSaving(true);
    setSaved(false);
    try {
      const updated = await preferencesAPI.updatePreferences(user.id, preferences);
      setPreferences(updated);
      setSaved(true);
      
      if (onPreferencesChange) {
        onPreferencesChange(updated);
      }

      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save preferences:', error);
      alert('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (key, value) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
  };

  // Account management handlers
  const handleDeactivate = async () => {
    if (!password) {
      setActionError('Password is required');
      return;
    }

    setActionLoading(true);
    setActionError('');
    
    try {
      await accountAPI.deactivateAccount(token, password, deactivateReason);
      setActionSuccess('Account deactivated successfully. You will be logged out.');
      
      setTimeout(() => {
        setShowDeactivateModal(false);
        if (onLogout) onLogout();
      }, 2000);
    } catch (error) {
      setActionError(error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!password) {
      setActionError('Password is required');
      return;
    }
    
    if (deleteConfirmation !== 'DELETE MY ACCOUNT') {
      setActionError('Please type "DELETE MY ACCOUNT" exactly to confirm');
      return;
    }

    setActionLoading(true);
    setActionError('');
    
    try {
      await accountAPI.deleteAccount(token, password, deleteConfirmation);
      setActionSuccess('Account deleted successfully. You will be logged out.');
      
      setTimeout(() => {
        setShowDeleteModal(false);
        if (onLogout) onLogout();
      }, 2000);
    } catch (error) {
      setActionError(error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) {
      setActionError('All fields are required');
      return;
    }
    
    if (newPassword !== confirmNewPassword) {
      setActionError('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setActionError('New password must be at least 6 characters');
      return;
    }

    setActionLoading(true);
    setActionError('');
    
    try {
      await accountAPI.changePassword(token, currentPassword, newPassword);
      setActionSuccess('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      
      setTimeout(() => {
        setShowPasswordModal(false);
        setActionSuccess('');
      }, 2000);
    } catch (error) {
      setActionError(error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleExportData = async () => {
    setActionLoading(true);
    setActionError('');
    
    try {
      const data = await accountAPI.exportData(token);
      
      // Download as JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `investorq-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setActionSuccess('Data exported successfully!');
      setTimeout(() => setActionSuccess(''), 3000);
    } catch (error) {
      setActionError(error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const resetModalState = () => {
    setPassword('');
    setDeactivateReason('');
    setDeleteConfirmation('');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
    setActionError('');
    setActionSuccess('');
  };

  if (!user) {
    return (
      <div style={{ 
        background: 'rgba(37, 99, 235, 0.1)', 
        border: '1px solid rgba(37, 99, 235, 0.3)', 
        borderRadius: '0.75rem', 
        padding: '3rem',
        textAlign: 'center'
      }}>
        <Settings size={48} color="#60a5fa" style={{ margin: '0 auto 1rem' }} />
        <h3 style={{ fontSize: '1.5rem', marginBottom: '0.75rem' }}>Login Required</h3>
        <p style={{ color: '#94a3b8', fontSize: '1rem' }}>
          Please log in to manage your preferences
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
        <Loader size={48} color="#60a5fa" className="spin" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)', 
        borderRadius: '0.75rem', 
        padding: '2rem', 
        marginBottom: '2rem'
      }}>
        <h2 style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Settings size={32} color="white" />
          User Preferences
        </h2>
        <p style={{ color: '#e9d5ff', fontSize: '1rem', margin: 0 }}>
          Customize your InvestorIQ experience
        </p>
      </div>

      {/* Success/Error Messages */}
      {actionSuccess && (
        <div style={{
          background: 'rgba(16, 185, 129, 0.1)',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          borderRadius: '0.5rem',
          padding: '1rem',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          color: '#10b981'
        }}>
          <CheckCircle size={20} />
          {actionSuccess}
        </div>
      )}

      {/* Preferences Form */}
      <div style={{ display: 'grid', gap: '2rem' }}>
        {/* Display Preferences */}
        <div style={{ 
          background: 'rgba(30, 41, 59, 0.5)', 
          border: '1px solid #334155', 
          borderRadius: '0.75rem', 
          padding: '1.5rem'
        }}>
          <h3 style={{ fontSize: '1.25rem', marginBottom: '1.5rem', fontWeight: '600' }}>Display Preferences</h3>
          
          <div style={{ display: 'grid', gap: '1.5rem' }}>
            {/* Default Tab */}
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', color: '#cbd5e1', marginBottom: '0.5rem', fontWeight: '500' }}>
                Default Tab on Login
              </label>
              <select
                value={preferences.default_tab}
                onChange={(e) => handleChange('default_tab', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#1e293b',
                  border: '1px solid #334155',
                  borderRadius: '0.5rem',
                  color: 'white',
                  fontSize: '1rem'
                }}
              >
                <option value="market">Market Overview</option>
                <option value="company">Company Analysis</option>
                <option value="compare">Stock Comparison</option>
                <option value="portfolio">Portfolio</option>
                <option value="watchlist">Watchlist</option>
              </select>
            </div>

            {/* Default Timeframe */}
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', color: '#cbd5e1', marginBottom: '0.5rem', fontWeight: '500' }}>
                Default Chart Timeframe
              </label>
              <select
                value={preferences.default_timeframe}
                onChange={(e) => handleChange('default_timeframe', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#1e293b',
                  border: '1px solid #334155',
                  borderRadius: '0.5rem',
                  color: 'white',
                  fontSize: '1rem'
                }}
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>

            {/* Currency */}
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', color: '#cbd5e1', marginBottom: '0.5rem', fontWeight: '500' }}>
                Currency Display
              </label>
              <select
                value={preferences.currency}
                onChange={(e) => handleChange('currency', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#1e293b',
                  border: '1px solid #334155',
                  borderRadius: '0.5rem',
                  color: 'white',
                  fontSize: '1rem'
                }}
              >
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
                <option value="GBP">GBP (£)</option>
                <option value="JPY">JPY (¥)</option>
                <option value="CAD">CAD (C$)</option>
              </select>
            </div>

            {/* Theme */}
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', color: '#cbd5e1', marginBottom: '0.5rem', fontWeight: '500' }}>
                Theme
              </label>
              <select
                value={preferences.theme}
                onChange={(e) => handleChange('theme', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#1e293b',
                  border: '1px solid #334155',
                  borderRadius: '0.5rem',
                  color: 'white',
                  fontSize: '1rem'
                }}
              >
                <option value="dark">Dark Mode</option>
                <option value="light">Light Mode (Coming Soon)</option>
              </select>
              <p style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.5rem' }}>
                Light mode will be available in a future update
              </p>
            </div>
          </div>
        </div>

        {/* Email Notifications */}
        <div style={{ 
          background: 'rgba(30, 41, 59, 0.5)', 
          border: '1px solid #334155', 
          borderRadius: '0.75rem', 
          padding: '1.5rem'
        }}>
          <h3 style={{ fontSize: '1.25rem', marginBottom: '1.5rem', fontWeight: '600' }}>Email Notifications</h3>
          
          <div style={{ display: 'grid', gap: '1rem' }}>
            {/* Master Email Toggle */}
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '1rem',
              padding: '1rem',
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '0.5rem',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={preferences.email_notifications}
                onChange={(e) => handleChange('email_notifications', e.target.checked)}
                style={{ width: '20px', height: '20px', cursor: 'pointer' }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', color: '#cbd5e1', marginBottom: '0.25rem' }}>
                  Enable Email Notifications
                </div>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                  Master switch for all email notifications
                </div>
              </div>
            </label>

            {/* Price Alerts */}
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '1rem',
              padding: '1rem',
              background: 'rgba(51, 65, 85, 0.3)',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              opacity: preferences.email_notifications ? 1 : 0.5
            }}>
              <input
                type="checkbox"
                checked={preferences.price_alert_emails}
                onChange={(e) => handleChange('price_alert_emails', e.target.checked)}
                disabled={!preferences.email_notifications}
                style={{ width: '20px', height: '20px', cursor: preferences.email_notifications ? 'pointer' : 'not-allowed' }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', color: '#cbd5e1', marginBottom: '0.25rem' }}>
                  Price Alert Notifications
                </div>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                  Get notified when your price alerts trigger
                </div>
              </div>
            </label>

            {/* Daily Summary */}
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '1rem',
              padding: '1rem',
              background: 'rgba(51, 65, 85, 0.3)',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              opacity: preferences.email_notifications ? 1 : 0.5
            }}>
              <input
                type="checkbox"
                checked={preferences.daily_summary_emails}
                onChange={(e) => handleChange('daily_summary_emails', e.target.checked)}
                disabled={!preferences.email_notifications}
                style={{ width: '20px', height: '20px', cursor: preferences.email_notifications ? 'pointer' : 'not-allowed' }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', color: '#cbd5e1', marginBottom: '0.25rem' }}>
                  Daily Portfolio Summary
                </div>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                  Receive daily email with your portfolio performance
                </div>
              </div>
            </label>
          </div>
        </div>

        {/* Save Button */}
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '0.75rem 2rem',
              background: saving ? '#6b7280' : '#10b981',
              border: 'none',
              borderRadius: '0.5rem',
              color: 'white',
              cursor: saving ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '1rem',
              fontWeight: '600'
            }}
          >
            {saving ? <Loader size={18} className="spin" /> : <Save size={18} />}
            {saving ? 'Saving...' : 'Save Preferences'}
          </button>

          {saved && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#10b981', fontSize: '0.875rem', fontWeight: '600' }}>
              <CheckCircle size={18} />
              Saved successfully!
            </div>
          )}
        </div>

        {/* ==================== ACCOUNT MANAGEMENT SECTION ==================== */}
        <div style={{ 
          background: 'rgba(30, 41, 59, 0.5)', 
          border: '1px solid #334155', 
          borderRadius: '0.75rem', 
          padding: '1.5rem',
          marginTop: '1rem'
        }}>
          <h3 style={{ 
            fontSize: '1.25rem', 
            marginBottom: '1.5rem', 
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <Shield size={20} color="#60a5fa" />
            Account Management
          </h3>

          {/* Account Info */}
          {accountStatus && (
            <div style={{
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '0.5rem',
              padding: '1rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{ marginBottom: '0.75rem' }}>
                <span style={{ color: '#94a3b8', fontSize: '0.875rem' }}>Email: </span>
                <span style={{ color: '#cbd5e1', fontWeight: '600' }}>{accountStatus.account.email}</span>
              </div>
              <div style={{ marginBottom: '0.75rem' }}>
                <span style={{ color: '#94a3b8', fontSize: '0.875rem' }}>Member since: </span>
                <span style={{ color: '#cbd5e1' }}>
                  {new Date(accountStatus.account.createdAt).toLocaleDateString()}
                </span>
              </div>
              <div style={{ 
                display: 'flex', 
                gap: '1.5rem', 
                marginTop: '1rem',
                paddingTop: '1rem',
                borderTop: '1px solid rgba(59, 130, 246, 0.2)'
              }}>
                <div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#60a5fa' }}>
                    {accountStatus.data.portfolios}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Portfolios</div>
                </div>
                <div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#60a5fa' }}>
                    {accountStatus.data.watchlistItems}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Watchlist</div>
                </div>
                <div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#60a5fa' }}>
                    {accountStatus.data.priceAlerts}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Alerts</div>
                </div>
              </div>
            </div>
          )}

          {/* Account Actions */}
          <div style={{ display: 'grid', gap: '1rem' }}>
            {/* Change Password */}
            <button
              onClick={() => { resetModalState(); setShowPasswordModal(true); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '1rem',
                background: 'rgba(51, 65, 85, 0.5)',
                border: '1px solid #475569',
                borderRadius: '0.5rem',
                color: '#cbd5e1',
                cursor: 'pointer',
                width: '100%',
                textAlign: 'left',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = '#60a5fa'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = '#475569'}
            >
              <Key size={20} color="#60a5fa" />
              <div>
                <div style={{ fontWeight: '600' }}>Change Password</div>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Update your account password</div>
              </div>
            </button>

            {/* Export Data */}
            <button
              onClick={handleExportData}
              disabled={actionLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '1rem',
                background: 'rgba(51, 65, 85, 0.5)',
                border: '1px solid #475569',
                borderRadius: '0.5rem',
                color: '#cbd5e1',
                cursor: actionLoading ? 'not-allowed' : 'pointer',
                width: '100%',
                textAlign: 'left',
                transition: 'all 0.2s',
                opacity: actionLoading ? 0.7 : 1
              }}
              onMouseEnter={(e) => !actionLoading && (e.currentTarget.style.borderColor = '#10b981')}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = '#475569'}
            >
              <Download size={20} color="#10b981" />
              <div>
                <div style={{ fontWeight: '600' }}>Export My Data</div>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Download all your data as JSON</div>
              </div>
            </button>

            {/* Deactivate Account */}
            <button
              onClick={() => { resetModalState(); setShowDeactivateModal(true); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '1rem',
                background: 'rgba(245, 158, 11, 0.1)',
                border: '1px solid rgba(245, 158, 11, 0.3)',
                borderRadius: '0.5rem',
                color: '#fbbf24',
                cursor: 'pointer',
                width: '100%',
                textAlign: 'left',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(245, 158, 11, 0.2)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(245, 158, 11, 0.1)'}
            >
              <UserX size={20} />
              <div>
                <div style={{ fontWeight: '600' }}>Deactivate Account</div>
                <div style={{ fontSize: '0.75rem', color: '#d97706' }}>
                  Temporarily disable your account. You can reactivate it later.
                </div>
              </div>
            </button>

            {/* Delete Account */}
            <button
              onClick={() => { resetModalState(); setShowDeleteModal(true); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '1rem',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '0.5rem',
                color: '#f87171',
                cursor: 'pointer',
                width: '100%',
                textAlign: 'left',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
            >
              <Trash2 size={20} />
              <div>
                <div style={{ fontWeight: '600' }}>Delete Account Permanently</div>
                <div style={{ fontSize: '0.75rem', color: '#dc2626' }}>
                  Permanently delete your account and all data. This cannot be undone.
                </div>
              </div>
            </button>
          </div>
        </div>
        {/* ==================== SUBSCRIPTION SECTION ==================== */}
<SubscriptionSettings user={user} token={token} />
      </div>

      {/* ==================== MODALS ==================== */}

      {/* Deactivate Account Modal */}
      {showDeactivateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '1rem'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            borderRadius: '1rem',
            padding: '2rem',
            maxWidth: '450px',
            width: '100%',
            border: '1px solid #475569'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#fbbf24' }}>
                <UserX size={24} />
                Deactivate Account
              </h3>
              <button
                onClick={() => setShowDeactivateModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}
              >
                <X size={24} />
              </button>
            </div>

            <p style={{ color: '#94a3b8', marginBottom: '1.5rem', lineHeight: '1.6' }}>
              Deactivating your account will:
            </p>
            <ul style={{ color: '#cbd5e1', marginBottom: '1.5rem', paddingLeft: '1.5rem', lineHeight: '1.8' }}>
              <li>Log you out immediately</li>
              <li>Hide your profile from other users</li>
              <li>Preserve your data for later reactivation</li>
              <li>Allow you to come back anytime by logging in</li>
            </ul>

            {actionError && (
              <div style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '0.5rem',
                padding: '0.75rem',
                marginBottom: '1rem',
                color: '#f87171',
                fontSize: '0.875rem'
              }}>
                {actionError}
              </div>
            )}

            {actionSuccess && (
              <div style={{
                background: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '0.5rem',
                padding: '0.75rem',
                marginBottom: '1rem',
                color: '#10b981',
                fontSize: '0.875rem'
              }}>
                {actionSuccess}
              </div>
            )}

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', color: '#cbd5e1', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                Reason (optional)
              </label>
              <select
                value={deactivateReason}
                onChange={(e) => setDeactivateReason(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#0f172a',
                  border: '1px solid #475569',
                  borderRadius: '0.5rem',
                  color: 'white',
                  fontSize: '0.875rem'
                }}
              >
                <option value="">Select a reason...</option>
                <option value="taking_break">Taking a break</option>
                <option value="not_useful">Not finding it useful</option>
                <option value="privacy">Privacy concerns</option>
                <option value="switching">Switching to another platform</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', color: '#cbd5e1', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                Enter your password to confirm
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#0f172a',
                  border: '1px solid #475569',
                  borderRadius: '0.5rem',
                  color: 'white',
                  fontSize: '0.875rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={() => setShowDeactivateModal(false)}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: '#334155',
                  border: 'none',
                  borderRadius: '0.5rem',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeactivate}
                disabled={actionLoading || !password}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: actionLoading || !password ? '#6b7280' : '#f59e0b',
                  border: 'none',
                  borderRadius: '0.5rem',
                  color: 'white',
                  cursor: actionLoading || !password ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                {actionLoading ? <Loader size={18} className="spin" /> : <UserX size={18} />}
                {actionLoading ? 'Processing...' : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '1rem'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            borderRadius: '1rem',
            padding: '2rem',
            maxWidth: '450px',
            width: '100%',
            border: '1px solid rgba(239, 68, 68, 0.5)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#ef4444' }}>
                <AlertTriangle size={24} />
                Delete Account Permanently
              </h3>
              <button
                onClick={() => setShowDeleteModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '0.5rem',
              padding: '1rem',
              marginBottom: '1.5rem'
            }}>
              <p style={{ color: '#f87171', margin: 0, fontWeight: '600', marginBottom: '0.5rem' }}>
                ⚠️ Warning: This action cannot be undone!
              </p>
              <p style={{ color: '#fca5a5', margin: 0, fontSize: '0.875rem' }}>
                All your data including portfolios, watchlists, alerts, and preferences will be permanently deleted.
              </p>
            </div>

            {actionError && (
              <div style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '0.5rem',
                padding: '0.75rem',
                marginBottom: '1rem',
                color: '#f87171',
                fontSize: '0.875rem'
              }}>
                {actionError}
              </div>
            )}

            {actionSuccess && (
              <div style={{
                background: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '0.5rem',
                padding: '0.75rem',
                marginBottom: '1rem',
                color: '#10b981',
                fontSize: '0.875rem'
              }}>
                {actionSuccess}
              </div>
            )}

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', color: '#cbd5e1', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                Enter your password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#0f172a',
                  border: '1px solid #475569',
                  borderRadius: '0.5rem',
                  color: 'white',
                  fontSize: '0.875rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', color: '#cbd5e1', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                Type <strong style={{ color: '#ef4444' }}>DELETE MY ACCOUNT</strong> to confirm
              </label>
              <input
                type="text"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder="DELETE MY ACCOUNT"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#0f172a',
                  border: `1px solid ${deleteConfirmation === 'DELETE MY ACCOUNT' ? '#ef4444' : '#475569'}`,
                  borderRadius: '0.5rem',
                  color: 'white',
                  fontSize: '0.875rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={() => setShowDeleteModal(false)}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: '#334155',
                  border: 'none',
                  borderRadius: '0.5rem',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={actionLoading || !password || deleteConfirmation !== 'DELETE MY ACCOUNT'}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: actionLoading || !password || deleteConfirmation !== 'DELETE MY ACCOUNT' 
                    ? '#6b7280' 
                    : '#ef4444',
                  border: 'none',
                  borderRadius: '0.5rem',
                  color: 'white',
                  cursor: actionLoading || !password || deleteConfirmation !== 'DELETE MY ACCOUNT' 
                    ? 'not-allowed' 
                    : 'pointer',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                {actionLoading ? <Loader size={18} className="spin" /> : <Trash2 size={18} />}
                {actionLoading ? 'Deleting...' : 'Delete Forever'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '1rem'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            borderRadius: '1rem',
            padding: '2rem',
            maxWidth: '450px',
            width: '100%',
            border: '1px solid #475569'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#60a5fa' }}>
                <Key size={24} />
                Change Password
              </h3>
              <button
                onClick={() => setShowPasswordModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}
              >
                <X size={24} />
              </button>
            </div>

            {actionError && (
              <div style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '0.5rem',
                padding: '0.75rem',
                marginBottom: '1rem',
                color: '#f87171',
                fontSize: '0.875rem'
              }}>
                {actionError}
              </div>
            )}

            {actionSuccess && (
              <div style={{
                background: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '0.5rem',
                padding: '0.75rem',
                marginBottom: '1rem',
                color: '#10b981',
                fontSize: '0.875rem'
              }}>
                {actionSuccess}
              </div>
            )}

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', color: '#cbd5e1', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                Current Password
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#0f172a',
                  border: '1px solid #475569',
                  borderRadius: '0.5rem',
                  color: 'white',
                  fontSize: '0.875rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', color: '#cbd5e1', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#0f172a',
                  border: '1px solid #475569',
                  borderRadius: '0.5rem',
                  color: 'white',
                  fontSize: '0.875rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', color: '#cbd5e1', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                placeholder="Confirm new password"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#0f172a',
                  border: `1px solid ${newPassword && confirmNewPassword && newPassword !== confirmNewPassword ? '#ef4444' : '#475569'}`,
                  borderRadius: '0.5rem',
                  color: 'white',
                  fontSize: '0.875rem',
                  boxSizing: 'border-box'
                }}
              />
              {newPassword && confirmNewPassword && newPassword !== confirmNewPassword && (
                <p style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                  Passwords do not match
                </p>
              )}
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={() => setShowPasswordModal(false)}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: '#334155',
                  border: 'none',
                  borderRadius: '0.5rem',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleChangePassword}
                disabled={actionLoading || !currentPassword || !newPassword || newPassword !== confirmNewPassword}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: actionLoading || !currentPassword || !newPassword || newPassword !== confirmNewPassword 
                    ? '#6b7280' 
                    : '#10b981',
                  border: 'none',
                  borderRadius: '0.5rem',
                  color: 'white',
                  cursor: actionLoading || !currentPassword || !newPassword || newPassword !== confirmNewPassword 
                    ? 'not-allowed' 
                    : 'pointer',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                {actionLoading ? <Loader size={18} className="spin" /> : <Key size={18} />}
                {actionLoading ? 'Updating...' : 'Update Password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Preferences;
